import React, { useCallback, useEffect, useState } from "react";
import { Image, Pressable, RefreshControl, ScrollView, StyleSheet, Text, View } from "react-native";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import MaterialCommunityIcons from "react-native-vector-icons/MaterialCommunityIcons";

import { DivineBackground } from "../../shared/ui/DivineBackground";
import { Header } from "../../shared/ui/Header";
import { ScoreCard } from "../../shared/ui/ScoreCard";
import { useAuth } from "../../../context/AuthContext";
import { UserRegistrationService } from "../../services/UserRegistrationService";
import { resolveEnumerationValue } from "../../shared/utils/dashboardBanner";

export function DashboardScreen() {
  const navigation = useNavigation<any>();
  const { user } = useAuth();
  const isGuru = user?.userType === "Guru";
  const [refreshing, setRefreshing] = useState(false);
  const [scoreRefreshKey, setScoreRefreshKey] = useState(0);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      setScoreRefreshKey((key) => key + 1);
    }, [])
  );

  useEffect(() => {
    let isMounted = true;

    const loadBanner = async () => {
      try {
        const response = await UserRegistrationService.getEnumeration("DashboardImage");
        const nextBannerUrl = resolveEnumerationValue(response?.data);

        if (isMounted) {
          setBannerUrl(nextBannerUrl);
        }
      } catch {
        if (isMounted) {
          setBannerUrl(null);
        }
      }
    };

    loadBanner();

    return () => {
      isMounted = false;
    };
  }, []);

  const onRefresh = () => {
    setRefreshing(true);
    setScoreRefreshKey((key) => key + 1);
  };

  const onScoreRefreshComplete = () => {
    setRefreshing(false);
  };

  return (
    <DivineBackground overlayColor="#FFF8E7" overlayOpacity={0.9}>
      <Header title="Gaur Prem Aashray" showProfile />

      <ScrollView
        contentContainerStyle={styles.container}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={["#F97316"]}
            tintColor="#F97316"
          />
        }
      >
        {bannerUrl ? (
          <View style={styles.bannerWrap}>
            <Image
              source={{ uri: bannerUrl }}
              style={styles.banner}
              resizeMode="cover"
              onError={() => setBannerUrl(null)}
            />
          </View>
        ) : null}

        <View style={styles.scoreWrap}>
          <ScoreCard
            refreshKey={scoreRefreshKey}
            onRefreshComplete={onScoreRefreshComplete}
          />
        </View>

        <View style={styles.dashboardSections}>
          <View style={styles.topRow}>
            <Pressable
              onPress={() => navigation.navigate("SadhanaTracker")}
              style={({ pressed }) => [
                styles.cardTracker,
                !isGuru && styles.cardTrackerFull,
                pressed && styles.cardPressed,
              ]}
            >
              <View style={[styles.cardIconWrap, styles.trackerIconWrap]}>
                <MaterialCommunityIcons
                  name="account-group-outline"
                  size={22}
                  color="#9333EA"
                />
              </View>
              <Text style={styles.cardTitleTracker}>Sadhana Tracker</Text>
              <Text style={styles.cardSub}>Approval view needed</Text>
            </Pressable>

            {isGuru ? (
              <Pressable
                onPress={() => navigation.navigate("AdminConsole")}
                style={({ pressed }) => [
                  styles.cardAdmin,
                  pressed && styles.cardPressed,
                ]}
              >
                <View style={[styles.cardIconWrap, styles.adminIconWrap]}>
                  <MaterialCommunityIcons
                    name="shield-account-outline"
                    size={22}
                    color="#2563EB"
                  />
                </View>
                <Text style={styles.cardTitleAdmin}>Admin Console</Text>
                <Text style={styles.cardSub}>View pending devotees</Text>
              </Pressable>
            ) : null}
          </View>

          <View style={styles.bottomRow}>
            <Pressable
              onPress={() => navigation.navigate("SadhanaMenu")}
              style={({ pressed }) => [
                styles.cardSadhana,
                pressed && styles.cardPressed,
              ]}
            >
              <View style={[styles.cardIconWrap, styles.sadhanaIconWrap]}>
                <Text style={styles.cardIconSparkle}>✦</Text>
              </View>
              <Text style={styles.cardTitleSadhana}>Sadhana</Text>
            </Pressable>

            <Pressable
              onPress={() => navigation.navigate("Health")}
              style={({ pressed }) => [
                styles.cardHealth,
                pressed && styles.cardPressed,
              ]}
            >
              <View style={[styles.cardIconWrap, styles.healthIconWrap]}>
                <Text style={styles.cardIconHeart}>❤</Text>
              </View>
              <Text style={styles.cardTitleHealth}>Health</Text>
            </Pressable>
          </View>
        </View>
      </ScrollView>
    </DivineBackground>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingHorizontal: 14,
    paddingTop: 14,
    paddingBottom: 18,
    gap: 14,
  },
  bannerWrap: {
    width: "100%",
    borderRadius: 18,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.18)",
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    elevation: 4,
  },
  banner: {
    width: "100%",
    height: 160,
  },
  scoreWrap: {
    marginTop: 2,
  },
  dashboardSections: {
    marginTop: 8,
    gap: 12,
  },
  topRow: {
    flexDirection: "row",
    gap: 12,
  },
  bottomRow: {
    flexDirection: "row",
    gap: 12,
  },
  cardPressed: {
    opacity: 0.92,
  },
  cardSadhana: {
    flex: 1,
    height: 135,
    borderRadius: 18,
    padding: 14,
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    backgroundColor: "rgba(253,242,248,0.98)",
    borderWidth: 1,
    borderColor: "rgba(244,114,182,0.26)",
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 9 },
    elevation: 4,
  },
  cardHealth: {
    flex: 1,
    height: 135,
    borderRadius: 18,
    padding: 14,
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    backgroundColor: "rgba(220,252,231,0.92)",
    borderWidth: 1,
    borderColor: "rgba(34,197,94,0.25)",
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 7 },
    elevation: 3,
  },
  cardTracker: {
    flex: 1,
    height: 135,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    backgroundColor: "rgba(243,232,255,0.92)",
    borderWidth: 1,
    borderColor: "rgba(192,132,252,0.28)",
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 7 },
    elevation: 3,
  },
  cardTrackerFull: {
    flex: 1,
  },
  cardAdmin: {
    flex: 1,
    height: 135,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    backgroundColor: "rgba(219,234,254,0.92)",
    borderWidth: 1,
    borderColor: "rgba(96,165,250,0.28)",
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 7 },
    elevation: 3,
  },
  cardIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
  },
  sadhanaIconWrap: {
    backgroundColor: "rgba(244,114,182,0.16)",
  },
  healthIconWrap: {
    backgroundColor: "rgba(34,197,94,0.18)",
  },
  trackerIconWrap: {
    backgroundColor: "rgba(147,51,234,0.12)",
  },
  adminIconWrap: {
    backgroundColor: "rgba(59,130,246,0.12)",
  },
  cardIconSparkle: {
    fontSize: 22,
    fontWeight: "900",
    color: "#BE185D",
  },
  cardIconHeart: {
    fontSize: 22,
    fontWeight: "900",
    color: "#14532D",
  },
  cardTitleSadhana: {
    fontSize: 16,
    fontWeight: "900",
    color: "#9D174D",
  },
  cardTitleHealth: {
    fontSize: 16,
    fontWeight: "900",
    color: "#1A4731",
  },
  cardTitleTracker: {
    fontSize: 15,
    fontWeight: "900",
    color: "#6B21A8",
    textAlign: "center",
  },
  cardTitleAdmin: {
    fontSize: 15,
    fontWeight: "900",
    color: "#1E3A8A",
    textAlign: "center",
  },
  cardSub: {
    fontSize: 11,
    fontWeight: "600",
    color: "#64748B",
    textAlign: "center",
  },
});
